/*
  Product grid card behaviour.

  1. Quick add
     Clicking a chip adds that variant straight to the cart and leaves the
     shopper on the page: the chip flips to a checkmark and the cart indicator
     updates. The cart drawer is deliberately NOT opened, so several items can
     be added in a row without interrupting browsing.

  2. Variant-split cards
     The "show variants on collection" app (eosh) clones each product card once
     per colour, client side, and stamps the clone with data-eosh-variant-id.
     Every clone is a copy of the SAME server-rendered markup, whose chips and
     swipe slides are the product's FIRST colour. syncCard() re-scopes both to
     the colour that clone is actually showing:

       - chips  -> that colour's sizes, with its own sold-out states
       - slides -> that colour's featured image plus the following two,
                   stopping before the next colour's run begins

     Variant and image data come from Shopify's own /products/<handle>.js,
     memoised per handle, so four Hatchet Short cards cost one request.

     This runs on every page, so it covers collections, search, the home page
     and anywhere else the app splits products out by variant.

  Both are bound with event delegation / a MutationObserver, so cards appended
  by Load More or injected by the app are handled with no re-initialisation.
*/
(function () {
  var RESET_MS = 1600;
  var MAX_SLIDES = 3;
  var SRCSET_WIDTHS = [180, 360, 540, 720, 1080];
  var productCache = {};

  /* ---------------------------------------------------------------- add to cart */

  function refreshCartUi() {
    document.dispatchEvent(new CustomEvent('cart:build'));

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-link__bubble-num').forEach(function (el) {
          el.innerText = cart.item_count;
        });
        document.querySelectorAll('.cart-link__bubble').forEach(function (bubble) {
          bubble.classList.toggle('cart-link__bubble--visible', cart.item_count > 0);
        });
      })
      .catch(function () {
        // The count refresh is cosmetic - never let it surface as a failure.
      });
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest ? evt.target.closest('.pnw-quick-add__size') : null;
    if (!btn || btn.disabled) return;

    evt.preventDefault();

    if (btn.classList.contains('is-loading')) return;

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    var originalLabel = btn.innerHTML;
    btn.classList.add('is-loading');

    var addUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

    fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then(function () {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.innerHTML = '✓';
        return refreshCartUi();
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        btn.innerHTML = '!';
      })
      .then(function () {
        setTimeout(function () {
          btn.classList.remove('is-added');
          btn.innerHTML = originalLabel;
        }, RESET_MS);
      });
  });

  /* ------------------------------------------------------- variant-split cards */

  /*
    Only the app's own attributes mark a card as showing one specific variant.

    Deliberately NOT inferred from links: the theme puts ?variant= on an
    ordinary card's link too (and on every colour swatch beneath it), so
    reading those would wrongly narrow a whole-product card - a colour-only
    product like the Serial Hat would collapse from two colour chips to one.

    Cards the THEME splits by variant need no help here: the Liquid template
    already renders the right chips for the variant it was handed.
  */
  function displayedVariantId(card) {
    return card.getAttribute('data-eosh-variant-id') ||
           card.getAttribute('swatch-default-variant') ||
           null;
  }

  function optionIndexes(product) {
    // /products/x.js returns options as strings on older shops, objects on newer.
    var names = (product.options || []).map(function (opt) {
      return String(opt && opt.name ? opt.name : opt).toLowerCase();
    });

    var idx = { size: -1, color: -1 };
    names.forEach(function (name, i) {
      if (name.indexOf('size') !== -1) idx.size = i;
      if (name.indexOf('color') !== -1 || name.indexOf('colour') !== -1) idx.color = i;
    });
    return idx;
  }

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('product fetch failed');
          return r.json();
        })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  function sized(url, width) {
    var clean = String(url).split('&width=')[0];
    return clean + (clean.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /*
    The images belonging to one colour: that variant's featured image and the
    ones following it, up to MAX_SLIDES, stopping before the next colour's
    featured image so a Green Camo card never reveals a black pant.

    A colour whose featured image is last in the list (Hatchet Pant's Green
    Camo, position 9 of 9) legitimately returns a single image.
  */
  function slidesForVariant(product, variant) {
    var images = product.images || [];
    if (!images.length || !variant.featured_image) return [];

    var start = variant.featured_image.position - 1;
    if (start < 0 || start >= images.length) return [];

    var boundary = images.length;
    product.variants.forEach(function (v) {
      if (!v.featured_image) return;
      var pos = v.featured_image.position - 1;
      if (pos > start && pos < boundary) boundary = pos;
    });

    return images.slice(start, Math.min(start + MAX_SLIDES, boundary));
  }

  function rebuildSlides(card, urls) {
    var strip = card.querySelector('.pnw-media-strip');
    if (!strip) return;

    var template = strip.querySelector('.pnw-media-strip__item');
    if (!template) return;

    /*
      Always clear first. The server rendered the FIRST colour's extra slides,
      so leaving them in place when this colour has none of its own is exactly
      how a black pant ends up on the camo card.
    */
    strip.querySelectorAll('.pnw-media-strip__item--extra').forEach(function (slide) {
      slide.remove();
    });

    urls.slice(1).forEach(function (url) {
      var slide = template.cloneNode(true);
      slide.classList.add('pnw-media-strip__item--extra');

      var img = slide.querySelector('img');
      if (!img) return;

      img.setAttribute('loading', 'lazy');
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.src = sized(url, 720);
      img.srcset = SRCSET_WIDTHS.map(function (w) {
        return sized(url, w) + ' ' + w + 'w';
      }).join(', ');

      strip.appendChild(slide);
    });
  }

  function syncCard(card) {
    var bar = card.querySelector('.pnw-quick-add');
    var handle = card.getAttribute('data-product-handle');
    var vid = displayedVariantId(card);
    if (!handle || !vid) return;

    // A card only needs syncing once per variant it displays.
    if (card.getAttribute('data-pnw-synced') === vid) return;
    card.setAttribute('data-pnw-synced', vid);

    getProduct(handle).then(function (product) {
      if (!product || !product.variants) return;

      var idx = optionIndexes(product);
      if (idx.color < 0) return; // single-dimension product - server markup is already right

      var current = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === String(vid)) { current = product.variants[i]; break; }
      }
      if (!current) return;

      var colour = current.options[idx.color];

      // ---- chips for this colour
      if (bar && bar.getAttribute('data-color') !== colour) {
        bar.setAttribute('data-color', colour);

        var label = bar.querySelector('.pnw-quick-add__label');
        if (label) {
          var base = label.getAttribute('data-base');
          if (base === null) {
            base = label.textContent.split('·')[0].trim();
            label.setAttribute('data-base', base);
          }
          label.textContent = base + ' · ' + colour;
        }

        var wrap = bar.querySelector('.pnw-quick-add__sizes');
        if (wrap) {
          wrap.textContent = '';
          product.variants.forEach(function (v) {
            if (v.options[idx.color] !== colour) return;
            var chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'pnw-quick-add__size';
            chip.setAttribute('data-variant-id', v.id);
            chip.setAttribute('aria-label', product.title + ' - ' + v.title);
            if (!v.available) chip.disabled = true;
            chip.textContent = idx.size >= 0 ? v.options[idx.size] : v.title;
            wrap.appendChild(chip);
          });
        }
      }

      // ---- swipe slides for this colour
      rebuildSlides(card, slidesForVariant(product, current));
    });
  }

  function syncAll() {
    document.querySelectorAll('.grid-product').forEach(syncCard);
  }

  var syncTimer = null;
  function scheduleSync() {
    if (syncTimer) return;
    syncTimer = setTimeout(function () {
      syncTimer = null;
      syncAll();
    }, 60);
  }

  function start() {
    syncAll();

    if (!('MutationObserver' in window) || !document.body) return;

    // The app injects and re-stamps its clones after the theme has rendered.
    new MutationObserver(scheduleSync).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-eosh-variant-id', 'swatch-default-variant']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
